import { Component, OnInit, Input, Output, OnChanges, EventEmitter, ViewEncapsulation } from '@angular/core';
import { CustomSpinnerService } from 'src/app/core/interceptors/custom-spinner.service';
import { HttpService } from 'src/app/core/services/http.service';
import { UtilService } from 'src/app/core/services/util.service';
import { HttpHeaders } from '@angular/common/http';
import { NgxFileDropEntry } from 'ngx-file-drop';
import * as pdfjsLib from 'pdfjs-dist';
import { random } from 'lodash';

(pdfjsLib as any).GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.js';
export interface UploadSummary {
  id: string,
  status: 'queue' | 'uploading' | 'uploaded' | 'failed',
  preSignedURL?: string,
  name: string,
  mimeType?: string,
  size: number,
  file?: File,
  documentType?: string,
  documentSubType?: string,
  approval?: string,
  createDate: Date,
  siteCode?: string,
  systemCode?: string,
  convertedS3Path?: string,
  approach?: string,
  progress?: number,
  error: string,
}
@Component({
    selector: 'app-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class FileUploadComponent {

  @Input() uploadSummary: UploadSummary[] = [];
  @Output() uploadSummaryChange = new EventEmitter<UploadSummary[]>();
  @Input('appConfig') appConfig: any = {
    autoUpload: false,
    multiple: false,
    maxFileSize: 10 * 1024 * 1024,
    queueLimit: 1,
    maxFileCount: 1,
    maxFileNameLength: 100,
    allowedMimeType: ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'application/msword', 'text/plain', 'image/tiff', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    allowedType: [ 'pdf', 'png', 'jpeg', 'jpg', 'doc', 'txt', 'tiff', 'docx']
};

  preSignInData: any;
  compId: number = random(0, 1000);


  // @Output() emitData: EventEmitter<Boolean> = new EventEmitter();
  // @Output() uploadData: EventEmitter<any> = new EventEmitter();

  constructor(
    private http: HttpService,
    private spin: CustomSpinnerService,
    private util: UtilService
  ) { }

  async dropped(ngxfiles: NgxFileDropEntry[]) {
    console.log(ngxfiles);
    const files: File[] = [];
    let isAllOk = true;
    for (const droppedFile of ngxfiles) {
      // Is it a file?
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        // fileEntry.file() is async — await each so files is populated before processFiles runs
        const file = await new Promise<File>((resolve) => fileEntry.file(resolve));
        files.push(file);
      } else {
        isAllOk = false;
        this.util.openSnackBar("Please try with files only.", 'Error');
      }
    }

    //process files
    if (isAllOk){
      this.processFiles(files);
    }
  }

  async processFiles(files: File[]) {
    let isPDFValid = await this.validatePDF(files);

    if (isPDFValid && this.validateUpload(files)) {
      files.map((x) => {
        this.pushToUploadSummary(x);
      });
    }
  }

  validatePDF(files: File[]){
    const q = files.map(async (file) => {
      if (file.type !== 'application/pdf') {
        return true;
      }
      return await this.checkPdfPassword(file);
    });
    return Promise.all(q).then(() => {
      return true;
    }).catch(() => {
      return false;
    });
  }
  checkPdfPassword(file: File): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const typedArray = new Uint8Array(reader.result as ArrayBuffer);
        try {
          await pdfjsLib.getDocument({ data: typedArray }).promise;
          resolve(true);
        } 
        catch (error: any) {
          console.log({ error });
          if (error?.name === 'PasswordException') {
            this.util.openSnackBar('The PDF is password-protected and cannot be uploaded.', 'Error');
            reject(false);
          } else {
            this.util.openSnackBar('Selected PDF cannot be uploaded.', 'Error');
            reject(false);
          }
        }
      };
      reader.readAsArrayBuffer(file);
    });
  }

  validateUpload(newUploads: File[]) {
    if (newUploads.length>1) {
      this.util.openSnackBar("Please drop one file at a time", 'Error');
      return false;
    }
    if (this.appConfig.maxFileCount < (newUploads.length + this.uploadSummary.length)) {
      this.util.openSnackBar("Max file upload limit is " + this.appConfig.maxFileCount, 'Error');
      return false;
    }
    if (!newUploads.every(this.validateSingleFile.bind(this))) {
      return false;
    }
    return true;
  }

  validateSingleFile(file: File): boolean {
    if (!file.size) {
      this.util.openSnackBar("The selected file does not have any content", 'Error');
      return false;
    }
    if (file.size >= this.appConfig.maxFileSize) {
      this.util.openSnackBar(`File size exceeds the maximum of ${(this.appConfig.maxFileSize/(1024 * 1024)).toFixed(0)} MB per file.You can upload mutiple files if required.`, "Error");
      return false;
    }
    if (!this.appConfig.allowedMimeType.includes(file.type)) {
      this.util.openSnackBar(`The selected file type is not a valid file type. The following file formats only are supported *`, "Error");
      return false;
    }
    if(file.name.length > this.appConfig.maxFileNameLength){
      this.util.openSnackBar("Filename should be less than or equal to "+this.appConfig.maxFileNameLength + ' characters', 'Error');
      return false;
    }
    return true;
  }

  pushToUploadSummary(file: File) {
    let fileSummary: UploadSummary = {
      id: '',
      status: 'queue',
      name: file.name,
      mimeType: file.type,
      size: file.size,
      file: file,
      createDate: new Date(),
      error: '',
    }
    this.uploadSummary.push(fileSummary);
    this.uploadSummaryChange.emit(this.uploadSummary);//emit to make two way binding
  }
  
  sendFile(file: File) {
    this.findPreSignedURL(file).subscribe({
      next: (response: any) => {
        let fileSummary: UploadSummary = response;
        fileSummary.status = "uploading";
        this.uploadSummary.push(fileSummary)
        this.uploadToS3(file, fileSummary);
      },
      error: (error: any) => {
        console.log({ error });
      }
    });
  }

  findPreSignedURL(file: File) {
    const filename = file.name;
    const filetype = file.type;
    const filesize = file.size;

    const payload = {
      "id": this.compId,
      "name": filename,
      "mimeType": filetype,
      "size": filesize,
    };
    return this.http.put('file', payload);
  }

  uploadToS3(file: File, fileSummary: UploadSummary) {
    this.spin.show();
    this.http.put(fileSummary.preSignedURL || '', file).subscribe({
      next: (x: any) => {
        console.log(x);
        this.spin.hide();
        fileSummary.status = "uploaded";
      },
      error: (error: any) => {
        console.log({ error });
        this.spin.hide();
        fileSummary.status = "failed";
        fileSummary.error = 'An unexpected error has occurred. Please try again.';
        this.util.openSnackBar(`S3 file upload is failed`, 'Error');
      }
    })
  }

  removeuploaded(item:UploadSummary) {
    // this.uploadSummary.splice(item, 1);
    let index = this.uploadSummary.findIndex(i=>i.id == item.id);
    const payload = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      body: {
        "id": item.id,
      }
    };
    this.http.delete('file', payload)
      .subscribe((x: any) => {
        this.http.delete(x.preSignedURL).subscribe();
        console.log(x);
        this.uploadSummary.splice(index, 1);
      });
  }

  viewDocument(item: any) {
    this.http.get('file', { id: item.id }).subscribe((x: any) => {
      if (x.preSignedURL) {
        this.util.urlToBlob(x.preSignedURL, {}, item.name).subscribe();
      }
    });
  }

  checkViewerRoles(item: any) {
    return true;
  }

  getProgressValue(uploadSummaryItem: UploadSummary){
    return uploadSummaryItem.status == 'uploaded' ? 100 : uploadSummaryItem.progress;
  }

}
